import { useState, useEffect, useCallback } from "react";
import { BilletYardOperator } from "../../models/authModels";

/**
 * Hook to manage operator selection and session storage
 * Handles localStorage persistence for operator selection across browser sessions
 */
export const useOperatorSession = () => {
  const [selectedOperatorId, setSelectedOperatorId] = useState<number | null>(
    null,
  );
  const [selectedOperatorName, setSelectedOperatorName] = useState<
    string | null
  >(null);
  const [isOperatorSelected, setIsOperatorSelected] = useState(false);

  // Storage key for localStorage
  const OPERATOR_STORAGE_KEY = "byi_selected_operator";

  /**
   * Load operator selection from localStorage on mount
   */
  useEffect(() => {
    try {
      const stored = localStorage.getItem(OPERATOR_STORAGE_KEY);
      if (stored) {
        const { operatorId, operatorName, selectedAt } = JSON.parse(stored);

        // Validate stored data
        if (operatorId && operatorName && typeof operatorId === "number") {
          setSelectedOperatorId(operatorId);
          setSelectedOperatorName(operatorName);
          setIsOperatorSelected(true);
        } else {
          localStorage.removeItem(OPERATOR_STORAGE_KEY);
        }
      }
    } catch (error) {
      localStorage.removeItem(OPERATOR_STORAGE_KEY);
    }
  }, []);

  /**
   * Select an operator and persist to localStorage
   */
  const selectOperator = useCallback((operator: BilletYardOperator) => {
    setSelectedOperatorId(operator.OperatorId);
    setSelectedOperatorName(operator.OperatorName);
    setIsOperatorSelected(true);

    // Persist to localStorage
    try {
      const operatorData = {
        operatorId: operator.OperatorId,
        operatorName: operator.OperatorName,
        selectedAt: new Date().toISOString(),
      };
      localStorage.setItem(OPERATOR_STORAGE_KEY, JSON.stringify(operatorData));
    } catch (error) {}
  }, []);

  /**
   * Clear operator selection and remove from localStorage
   */
  const clearOperatorSelection = useCallback(() => {
    setSelectedOperatorId(null);
    setSelectedOperatorName(null);
    setIsOperatorSelected(false);

    try {
      localStorage.removeItem(OPERATOR_STORAGE_KEY);
    } catch (error) {}
  }, []);

  /**
   * Switch to a different operator (convenience method)
   */
  const switchOperator = useCallback(
    (operator: BilletYardOperator) => {
      selectOperator(operator);
    },
    [selectedOperatorId, selectOperator],
  );

  /**
   * Get current operator as BilletYardOperator object
   */
  const getCurrentOperator = useCallback((): BilletYardOperator | null => {
    if (!selectedOperatorId || !selectedOperatorName) {
      return null;
    }
    return {
      OperatorId: selectedOperatorId,
      OperatorName: selectedOperatorName,
    };
  }, [selectedOperatorId, selectedOperatorName]);

  /**
   * Check if a specific operator is currently selected
   */
  const isOperatorSelectedById = useCallback(
    (operatorId: number): boolean => {
      return selectedOperatorId === operatorId;
    },
    [selectedOperatorId],
  );

  return {
    // Current selection state
    selectedOperatorId,
    selectedOperatorName,
    isOperatorSelected,

    // Selection management
    selectOperator,
    clearOperatorSelection,
    switchOperator,

    // Utility methods
    getCurrentOperator,
    isOperatorSelectedById,

    // Debug info
    storageKey: OPERATOR_STORAGE_KEY,
  };
};
